import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
  Tooltip,
  SelectChangeEvent,
} from '@mui/material'
import { GridRowId } from '@mui/x-data-grid-premium'
import { useNavigate } from 'react-router-dom'
import DataGridPremiumDemo from '@/components/Table/DataGrid'
import PageHeaderWithIcons from '@/components/heder/PageHeaderWithIcons'
import columnsConfig from '@/components/features/config/columns/sensors/column.config'
import { MenuItemData } from '@/mockdata/sensors/sensorsMenuItem'
import { getAllSensors } from '@/local_db/db'
import { useAppTheme } from '@/theme/ThemeContext'
import { debugLog } from '@/utils/debugLog'

type RowSelectionModelV7 = { type: 'include' | 'exclude'; ids: Set<GridRowId> }

const Sensors: React.FC = () => {
  const theme = useTheme()
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const navigate = useNavigate()
  const [sensors, setSensors] = useState<any[]>([])
  const [type, setType] = React.useState<string>('')
  const [selection, setSelection] = React.useState<RowSelectionModelV7>({
    type: 'include',
    ids: new Set(),
  })

  // Dark mode colors
  const colors = {
    filterBarBg: isDarkMode ? '#1A2942' : '#F7FAFD',
    btnBg: isDarkMode ? '#3B82F6' : '#E3F5FF',
    btnHover: isDarkMode ? '#2563EB' : '#D4EDFF',
    btnText: isDarkMode ? '#fff' : '#003F61',
    btnBorder: isDarkMode ? '#3B82F6' : '#E3F5FF',
    outlineBtnBg: isDarkMode ? '#202B40' : '#E3F5FF',
    outlineBtnBorder: isDarkMode ? '#445876' : '#E4E7EB',
    disabledText: isDarkMode ? '#6B7A8C' : '#C0C2D6',
    labelColor: isDarkMode ? '#93A3B5' : undefined,
    inputBg: isDarkMode ? '#162645' : undefined,
    inputBorder: isDarkMode ? '#445876' : undefined,
    menuBg: isDarkMode ? '#162645' : undefined,
    menuItemHover: isDarkMode ? '#202B40' : undefined,
  }

  useEffect(() => {
    fetchSensors()
  }, [])

  const fetchSensors = async () => {
    const data = await getAllSensors()
    debugLog(data)
    setSensors(data)
  }

  const [selectedCount, setSelectedCount] = React.useState(0)

  const handleChange = (e: SelectChangeEvent) =>
    setType(e.target.value as string)

  const resolvedIds = React.useMemo(() => {
    const idsAll = sensors.map(
      (r: any) => r.id ?? r._id ?? r.key ?? r.uid ?? r.guid
    )
    if (selection.type === 'include') return Array.from(selection.ids)
    const excluded = selection.ids
    return idsAll.filter(id => !excluded.has(id))
  }, [selection, sensors])

  const disabled = !type || selectedCount === 0

  const goToGraph = () => {
    if (disabled) return
    navigate('/sensors/graph', {
      state: { type, ids: resolvedIds },
    })
  }

  const columns = columnsConfig

  const tooltipTitle = (
    <Box sx={{ textAlign: 'center', lineHeight: 1.4 }}>
      כדי לראות גרף יש לבחור
      <br />
      את סוג החיישן לתצוגה ולפחות
      <br />
      שורה אחת מהטבלה
    </Box>
  )

  return (
    <Box
      width="100%"
      sx={{
        display: 'flex',
        backgroundColor: theme.palette.background.default,
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%',
        minHeight: 0,
      }}
    >
      <PageHeaderWithIcons title="חיישנים" />
      <Box sx={{ width: '100%', flex: 1, minHeight: 0, px: 'clamp(8px, 3.5vw, 72px)', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            direction: 'rtl',
            bgcolor: colors.filterBarBg,
            p: 'clamp(8px, 1vw, 16px)',
            borderRadius: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 'clamp(8px, 1vw, 20px)',
            flexWrap: 'wrap',
          }}
        >
          <FormControl size="small" sx={{ 
            minWidth: 'clamp(140px, 13vw, 220px)',
            '& .MuiInputLabel-root': { color: colors.labelColor },
            '& .MuiOutlinedInput-root': {
              bgcolor: colors.inputBg,
              '& fieldset': { borderColor: colors.inputBorder },
            },
            '& .MuiSelect-icon': { color: colors.labelColor },
          }}>
            <InputLabel id="edge-type-label">סוג יח. קצה</InputLabel>
            <Select
              labelId="edge-type-label"
              value={type}
              label="סוג יח. קצה"
              onChange={handleChange}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: colors.menuBg,
                    '& .MuiMenuItem-root:hover': { bgcolor: colors.menuItemHover },
                  },
                },
              }}
            >
              <MenuItem value="">— בחר —</MenuItem>
              {MenuItemData.map(i => (
                <MenuItem key={i.value} value={i.value}>
                  {i.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            size="small"
            sx={{
              mr: 0,
              minWidth: 'clamp(64px, 6vw, 96px)',
              height: 'clamp(32px, 2.6vw, 42px)',
              fontSize: 'clamp(12px, 0.9vw, 16px)',
              border: `1px solid ${colors.btnBorder}`,
              bgcolor: colors.btnBg,
              color: colors.btnText,
              boxShadow: 'none',
              '&:hover': { bgcolor: colors.btnHover, boxShadow: 'none' },
            }}
          >
            חיפוש
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip
            title={disabled ? tooltipTitle : ''}
            placement="top"
            arrow
            disableHoverListener={!disabled}
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: isDarkMode ? 'rgba(26,41,66,0.95)' : 'rgba(0,63,97,0.8)',
                  color: '#EFF2F7',
                  fontSize: 12,
                  borderRadius: 0.5,
                  px: 1,
                  py: 0.5,
                  textAlign: 'center',
                },
              },
              arrow: { sx: { color: isDarkMode ? 'rgba(26,41,66,0.95)' : 'rgba(0,63,97,0.8)' } },
            }}
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                disabled={disabled}
                onClick={goToGraph}
                sx={{
                  color: disabled ? colors.disabledText : colors.btnText,
                  borderColor: colors.outlineBtnBorder,
                  bgcolor: disabled ? 'inherit' : colors.outlineBtnBg,
                  '&:hover': { bgcolor: colors.btnHover },
                }}
              >
                הצגת גרף
              </Button>
            </span>
          </Tooltip>
        </Box>
        <Box sx={{ width: '100%', flex: 1, minHeight: 0 }}>
          <DataGridPremiumDemo
            columns={columns}
            rows={sensors}
            hideFooter
            width="100%"
            headerPosition="bottom"
            pageSize={50}
            checkboxSelection
            rowSelectionModel={selection}
            onRowSelectionModelChange={setSelection}
            onSelectedCountChange={setSelectedCount}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default Sensors
